/**
 * DECODER TEXT COMPONENT
 * =====================
 * 
 * Creates a "Matrix-style" text decoding animation where random Japanese katakana 
 * characters gradually resolve into the final text. This creates a cyberpunk/hacker
 * aesthetic that's perfect for tech portfolios.
 * 
 * VISUAL EFFECT:
 * - Text starts as random katakana characters
 * - Characters "decode" from left to right 
 * - Final text is revealed progressively
 * - Creates illusion of data being decrypted
 * 
 * ANIMATION FLOW:
 * Random characters → Flickering → Final text character-by-character
 */

import { useRef, useEffect, memo } from 'react';
import classNames from 'classnames';
import { usePrefersReducedMotion } from 'hooks';
import { spring, chain, delay, value } from 'popmotion';
import VisuallyHidden from 'components/VisuallyHidden';
import prerender from 'utils/prerender';
import './index.css';

/**
 * KATAKANA CHARACTER SET
 * =====================
 * Japanese katakana characters used for the "random" decoding effect
 * These create the authentic cyberpunk/Matrix aesthetic
 * 
 * Character Types:
 * - Basic katakana (ア-ン)
 * - Voiced variants (ガ-ポ) 
 * - Long vowel mark (ー)
 */
// prettier-ignore
const glyphs = [
  'ア', 'イ', 'ウ', 'エ', 'オ',  // A, I, U, E, O
  'カ', 'キ', 'ク', 'ケ', 'コ',  // KA, KI, KU, KE, KO
  'サ', 'シ', 'ス', 'セ', 'ソ',  // SA, SHI, SU, SE, SO
  'タ', 'チ', 'ツ', 'テ', 'ト',  // TA, CHI, TSU, TE, TO
  'ナ', 'ニ', 'ヌ', 'ネ', 'ノ',  // NA, NI, NU, NE, NO
  'ハ', 'ヒ', 'フ', 'ヘ', 'ホ',  // HA, HI, FU, HE, HO
  'マ', 'ミ', 'ム', 'メ', 'モ',  // MA, MI, MU, ME, MO
  'ヤ', 'ユ', 'ヨ', 'ー',        // YA, YU, YO, long vowel
  'ラ', 'リ', 'ル', 'レ', 'ロ',  // RA, RI, RU, RE, RO
  'ワ', 'ヰ', 'ヱ', 'ヲ', 'ン',  // WA, WI, WE, WO, N
  'ガ', 'ギ', 'グ', 'ゲ', 'ゴ',  // GA, GI, GU, GE, GO (voiced)
  'ザ', 'ジ', 'ズ', 'ゼ', 'ゾ',  // ZA, JI, ZU, ZE, ZO (voiced)
  'ダ', 'ヂ', 'ヅ', 'デ', 'ド',  // DA, DJI, DZU, DE, DO (voiced)
  'バ', 'ビ', 'ブ', 'ベ', 'ボ',  // BA, BI, BU, BE, BO (voiced)
  'パ', 'ピ', 'プ', 'ペ', 'ポ',  // PA, PI, PU, PE, PO (semi-voiced)
];

/**
 * CHARACTER TYPES
 * ===============
 * Defines the two states a character can be in during animation
 */
const CharType = {
  Glyph: 'glyph',  // Random katakana character (still "decoding")
  Value: 'value',  // Final decoded character (revealed)
};

/**
 * CORE ANIMATION LOGIC
 * ===================
 * This function determines what each character position should display
 * based on the current animation progress
 * 
 * @param {string[]} content - Array of characters in the target text
 * @param {Object[]} output - Current state of displayed characters  
 * @param {number} position - Animation progress (0 to content.length)
 * @returns {Object[]} New character states for rendering
 */
function shuffle(content, output, position) {
  return content.map((value, index) => {
    // DECODED ZONE: Characters before current position are fully revealed
    if (index < position) {
      return { type: CharType.Value, value }; // Show final character
    }

    // FLICKERING EFFECT: Create randomness in the "decoding" characters
    // When position is between integers (e.g., 1.3, 2.7), create flicker
    if (position % 1 < 0.5) {
      // Show random katakana character
      const rand = Math.floor(Math.random() * glyphs.length);
      return { type: CharType.Glyph, value: glyphs[rand] };
    }

    // STABLE GLYPH: Keep previous random character briefly for smoother effect
    return { type: CharType.Glyph, value: output[index].value };
  });
}

/**
 * DECODER TEXT COMPONENT
 * ======================
 * 
 * PROPS:
 * @param {string} text - The final text to reveal after decoding
 * @param {boolean} start - Whether to start the animation immediately  
 * @param {number} delay - Delay before animation starts (ms)
 * @param {string} className - Additional CSS classes
 */
const DecoderText = ({
  text,
  start = true,
  delay: startDelay = 0,
  className,
  ...rest
}) => {
  // STATE MANAGEMENT
  const output = useRef([{ type: CharType.Glyph, value: '' }]); // Current character states
  const container = useRef();           // DOM reference for direct manipulation
  const reduceMotion = usePrefersReducedMotion(); // Accessibility preference

  /**
   * MAIN ANIMATION EFFECT
   * ====================
   * Sets up and manages the entire decoding animation sequence
   */
  useEffect(() => {
    const containerInstance = container.current;
    const content = text.split(''); // Convert text to character array
    let animation; // Animation controller reference

    /**
     * DOM RENDERING FUNCTION
     * =====================
     * Converts character state array to HTML and injects into DOM
     * Uses direct innerHTML manipulation for performance
     */
    const renderOutput = () => {
      // Map each character to a styled span element
      const characterMap = output.current.map(item => {
        // CSS class determines styling: .decoder-text__glyph or .decoder-text__value
        return `<span class="decoder-text__${item.type}">${item.value}</span>`;
      });

      // Direct DOM manipulation for smooth 60fps animation
      containerInstance.innerHTML = characterMap.join('');
    };

    /**
     * ANIMATION VALUE CONTROLLER
     * =========================
     * This is where the magic happens - links animation progress to visual updates
     */
    const springValue = value(0, position => {
      // Update character states based on current animation position
      output.current = shuffle(content, output.current, position);
      // Immediately render the updated characters
      renderOutput();
    });

    /**
     * ANIMATION SEQUENCE SETUP
     * =======================
     * Creates the spring animation that drives the decoding effect
     */
    if (start && !animation && !reduceMotion && !prerender) {
      animation = chain(
        delay(startDelay), // Wait for specified delay
        spring({
          from: 0,              // Start at position 0 (no characters decoded)
          to: content.length,   // End at full text length (all characters decoded)
          stiffness: 8,         // How quickly animation progresses (lower = slower)
          damping: 5,           // How much the animation oscillates (higher = smoother)
        })
      ).start(springValue); // Begin animation and connect to value controller
    }

    /**
     * ACCESSIBILITY FALLBACK
     * =====================
     * For users who prefer reduced motion, show final text immediately
     */
    if (reduceMotion) {
      output.current = content.map((value, index) => ({
        type: CharType.Value,  // All characters are final values
        value: content[index], // Show actual text characters
      }));
      renderOutput(); // Render static text
    }

    // CLEANUP: Stop animation when component unmounts or dependencies change
    return () => {
      if (animation) {
        animation.stop();
      }
    };
  }, [reduceMotion, start, startDelay, text]);

  /**
   * COMPONENT RENDER
   * ===============
   * Accessible structure with hidden label and animated content
   */
  return (
    <span className={classNames('decoder-text', className)} {...rest}>
      {/* 
        ACCESSIBILITY: Hidden text for screen readers
        Screen readers get the final text immediately, while sighted users see animation
      */}
      <VisuallyHidden className="decoder-text__label">{text}</VisuallyHidden>
      
      {/* 
        ANIMATED CONTENT: Hidden from screen readers, shows visual animation
        This container gets its innerHTML updated directly for performance
      */}
      <span aria-hidden className="decoder-text__content" ref={container} />
    </span>
  );
};

export default memo(DecoderText);

/**
 * TECHNICAL IMPLEMENTATION DETAILS
 * ===============================
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Direct DOM manipulation via innerHTML (faster than React re-renders)
 * - Memoized component to prevent unnecessary re-renders
 * - Animation only runs when component is in use
 * 
 * ANIMATION MECHANICS:
 * ===================
 * 
 * 1. INITIALIZATION:
 *    - Text split into individual characters
 *    - Each character starts as random katakana
 * 
 * 2. SPRING ANIMATION:
 *    - Progress value goes from 0 to text.length
 *    - Each frame calls shuffle() with current progress
 *    - Characters before progress position are "decoded"
 *    - Characters after progress position remain random
 * 
 * 3. VISUAL STATES:
 *    position: 0.0 → "ンカヨテス" (all random)
 *    position: 1.5 → "Hンラミセ" (first char decoded, rest random)  
 *    position: 3.0 → "Helテホ" (first 3 decoded, rest random)
 *    position: 5.0 → "Hello" (all decoded)
 * 
 * 4. FLICKERING EFFECT:
 *    - When position has decimal (1.3, 2.7), creates flicker
 *    - Random characters change rapidly for "decoding" appearance
 *    - When position is whole number, characters stabilize briefly
 * 
 * CSS STYLING:
 * ============
 * - .decoder-text__glyph: Styles for random katakana (often dim/colored)
 * - .decoder-text__value: Styles for final characters (bright/normal)
 * - Smooth transitions between states create polished effect
 * 
 * ACCESSIBILITY:
 * =============
 * - Screen readers get immediate access to final text
 * - Reduced motion users see static text
 * - Animation respects system preferences
 * - Semantic markup maintains text meaning
 * 
 * USE CASES:
 * ==========
 * Perfect for:
 * - Tech/cyberpunk portfolios
 * - Loading states with style
 * - Attention-grabbing headlines
 * - Sci-fi themed interfaces
 * - "Hacker" aesthetic elements
 */
